import React, { useCallback, useEffect, useRef } from 'react'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { EnvConfig } from '../config/EnvConfig'
import turnstileScript from '../templates/externalScripts/turnstileScript'
import PropTypes from 'prop-types'
import { Logger } from '../utils/loggerUtils'
 
const TURNSTILE_ID = 'turnstileCaptchaInvisible'
const DEFAULT_ACTION = 'REGISTRATION'

const logger = new Logger.Builder()
  .setContext('TurnstileInvisible')
  .build()

export const useTurnstileInvisibleOn = ({ id = TURNSTILE_ID } = {}) => {
  const captchaTurnstileOn = useFeatureValue('turnstile_invisible_feature', false)

  const reloadCaptcha = useCallback(() => {
    if (!captchaTurnstileOn) return
    
    try {
      const captchaElement = document.querySelector(`#${id}`)      
      if (captchaElement && window?.turnstile?.reset) {
        window.turnstile.reset(`#${id}`)
      }
    } catch (error) {
      logger.error(new Error(`Error reloading Turnstile ${id}: ${error.message}`))
    }
  }, [captchaTurnstileOn, id])

  return {
    captchaTurnstileOn,
    reloadCaptcha
  }
}

export const TurnstileInvisible = ({ 
  action = DEFAULT_ACTION,
  id = TURNSTILE_ID,
  execution = 'render',
  onCaptchaVerified = () => null, 
  onCaptchaExpired = () => null,
}) => {
  const scriptRef = useRef(null)
  const { captchaTurnstileOn } = useTurnstileInvisibleOn({ id })

  const isTurnstileAlreadyRendered = useCallback(() => {
    const turnstileElement = document.getElementById(id)
    const hasRenderedWidget = turnstileElement && turnstileElement.children.length > 0
    
    return hasRenderedWidget
  }, [id])

  const initializeTurnstile = useCallback(() => {
    if (isTurnstileAlreadyRendered() || !window?.turnstile) return

    try {
      const widgetId = window.turnstile.render(`#${id}`, {
        action,
        execution,
        sitekey: EnvConfig.GATSBY_CLOUDFARE_INVISIBLE_CAPTCHA_SITEKEY,
        theme: 'light',
        callback: onCaptchaVerified,
        'expired-callback': onCaptchaExpired,
        'error-callback': (err) => {
          logger.error(new Error(`Turnstile error: ${err || 'Unknown error'}`))
        },
      })
    } catch (error) {
      logger.error(new Error(`Failed to render Turnstile ${id}: ${error.message}`))
    }
  }, [id, action, execution, onCaptchaVerified, onCaptchaExpired, isTurnstileAlreadyRendered])

  const loadTurnstileScript = useCallback(() => {
    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com"]')
    if (existingScript) {
      scriptRef.current = existingScript
      initializeTurnstile()
      return existingScript
    }

    const script = turnstileScript(initializeTurnstile)
    document.head.appendChild(script)
    scriptRef.current = script
    return script
  }, [initializeTurnstile])

  const destroyCaptchaScript = useCallback(() => {
    if (scriptRef.current && document.head.contains(scriptRef.current)) {
      document.head.removeChild(scriptRef.current)
      scriptRef.current = null
    }
  }, [id])

  useEffect(() => {
    if (!captchaTurnstileOn || isTurnstileAlreadyRendered()) return

    const script = loadTurnstileScript()

    return () => {
      destroyCaptchaScript()
    }
  }, [captchaTurnstileOn, loadTurnstileScript, isTurnstileAlreadyRendered, destroyCaptchaScript])

  if (!captchaTurnstileOn) return null

  return (
    <div id={id} style={{ display: 'none' }} />
  )
}

TurnstileInvisible.propTypes = {
  action: PropTypes.string,
  execution: PropTypes.string,
  onCaptchaVerified: PropTypes.func,
  onCaptchaExpired: PropTypes.func,
  id: PropTypes.string,
}
